/**
 * SwapFade — the crossfade of a wall that has just answered a new query. The cells that changed fade from the old
 * picture to the new one; a cell that keeps its place is the same picture under the old layer, so it does not move or
 * blink; nothing is animated into position (no shuffle).
 *
 * The old pictures are laid over the new wall for FADE_MS, at the rectangles the previous layout gave them, and fade
 * out. `useSwapFade` takes the previous layout's snapshot when the painted query changes, before the browser paints the
 * new one.
 */
import {useEffect, useLayoutEffect, useRef, useState} from 'react'
import type {JustifiedRow, LayoutTile} from './justify'
import styles from './SwapFade.module.scss'

export const FADE_MS = 220

export type FadeRect = {left: number; top: number; width: number; height: number; src: string}

/** The pictures of the rows in the window, each at its rectangle in the wall: the cells without a picture to lay over
 *  (the ghost, a take at work) are left out. `srcOf` names a cell's picture, or `null`. */
export function fadeRects<C extends LayoutTile>(
    rows: readonly JustifiedRow<C>[], srcOf: (cell: C) => string | null, gap: number, inWindow: (top: number, bottom: number) => boolean,
): FadeRect[] {
    const rects: FadeRect[] = []
    let top = 0
    for (const row of rows) {
        const bottom = top + row.height
        if (inWindow(top, bottom)) {
            let left = 0
            for (const {tile, width} of row.cells) {
                const src = srcOf(tile)
                if (src) rects.push({left, top, width, height: row.height, src})
                left += width + gap
            }
        }
        top = bottom + gap
    }
    return rects
}

export type Leaving = {rects: FadeRect[]; swap: number}

/** The previous layout's pictures, for FADE_MS after `key` (the answered query) changes; `null` otherwise. `swap` counts
 *  the swaps, the layer's key: a swap that lands during the last one's fade starts its own. */
export function useSwapFade(key: string, snapshot: () => FadeRect[]): Leaving | null {
    const [leaving, setLeaving] = useState<Leaving | null>(null)
    const swaps = useRef(0)
    const held = useRef({key, snapshot})
    useLayoutEffect(() => {
        const before = held.current
        held.current = {key, snapshot}
        if (before.key === key) return
        const rects = before.snapshot()
        if (rects.length > 0) setLeaving({rects, swap: ++swaps.current})
    })
    useEffect(() => {
        if (!leaving) return
        const timer = setTimeout(() => setLeaving(null), FADE_MS + 60)
        return () => clearTimeout(timer)
    }, [leaving])
    return leaving
}

export function SwapFade({rects}: {rects: FadeRect[]}) {
    return (
        <div className={styles.layer} aria-hidden style={{animationDuration: `${FADE_MS}ms`}}>
            <div className={styles.inner}>
                {rects.map((r, i) => (
                    <img
                        key={i}
                        className={styles.cell}
                        src={r.src}
                        alt=""
                        style={{left: r.left, top: r.top, width: r.width, height: r.height}}
                    />
                ))}
            </div>
        </div>
    )
}
